/**
 * Bilingual-by-construction i18n (doc 10): every key carries {en, ar} in one
 * object, so a missing translation is a type error, not a runtime fallback.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { formatFils as canonicalFormatFils, toDirhamSymbol } from '@servicesai/shared';

export type Lang = 'en' | 'ar';
export interface Localized {
  en: string;
  ar: string;
}

interface I18nContextValue {
  lang: Lang;
  dir: 'ltr' | 'rtl';
  /**
   * The citizen asked for this language. Anything downstream that mirrors the
   * shell onto durable state — a conversation, a stored preference — may only
   * act on a change made through here.
   */
  setLang: (lang: Lang) => void;
  /**
   * Show the shell in a language somebody else decided: a `?lang=` on a shared
   * link, the language a conversation is already being held in, a journey page
   * localizing itself. Identical on screen, and deliberately not a request to
   * change anything else.
   */
  adoptLang: (lang: Lang) => void;
  /**
   * Advances only on `setLang`. A reader that has seen a given value has seen
   * every language request the citizen has made so far.
   */
  langIntent: number;
  /** Localize a bilingual object. */
  l: (value: Localized | undefined | null) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ defaultLang = 'ar', children }: { defaultLang?: Lang; children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('sai.lang') : null;
    return stored === 'en' || stored === 'ar' ? stored : defaultLang;
  });
  const [langIntent, setLangIntent] = useState(0);

  const adoptLang = useCallback((next: Lang) => {
    setLangState(next);
    localStorage.setItem('sai.lang', next);
  }, []);

  const setLang = useCallback(
    (next: Lang) => {
      adoptLang(next);
      setLangIntent((n) => n + 1);
    },
    [adoptLang],
  );

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  const value = useMemo<I18nContextValue>(
    () => ({
      lang,
      dir: lang === 'ar' ? 'rtl' : 'ltr',
      setLang,
      adoptLang,
      langIntent,
      l: (v) => (v ? v[lang] : ''),
    }),
    [lang, setLang, adoptLang, langIntent],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/**
 * Format fils as a dirham amount for the screen (western digits per doc 10 §3).
 *
 * This used to be a hand-copied duplicate of the shared formatter and has been
 * reduced to the canonical one plus the display transform, so the two can no
 * longer drift. The output differs on purpose: the shared function writes the
 * "AED 150" the engine grounds its replies against, and everything a citizen
 * reads on a screen leads with the dirham sign instead.
 */
export function formatFils(amountFils: number, lang: Lang): string {
  return toDirhamSymbol(canonicalFormatFils(amountFils, lang));
}

/**
 * The same, for the housing surfaces that carry whole dirhams rather than fils.
 * They each used to interpolate `AED ${n.toLocaleString('en-AE')}` by hand,
 * which is how they came to print a Latin "AED" on Arabic pages.
 */
export function formatAed(amountAed: number, lang: Lang): string {
  return formatFils(Math.round(amountAed * 100), lang);
}

export function formatDate(iso: string, lang: Lang): string {
  return new Intl.DateTimeFormat(lang === 'ar' ? 'ar-AE' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    numberingSystem: 'latn',
  }).format(new Date(iso));
}

/**
 * A day with no time on it.
 *
 * Distinct from `formatDate` because half of what the platform shows a citizen is a day
 * rather than a moment: a date of birth, a passport expiry, a decision date. Putting
 * `formatDate` on one of those renders "1 Jan 1985, 00:00", where the midnight is not a
 * fact the platform holds — it is the absence of one, displayed.
 *
 * Western digits in both languages, matching every other number the platform shows: an
 * Arabic-Indic date beside a Latin reference reads as two systems, and the reference is
 * the one that cannot change.
 *
 * Returns the input unchanged when it is not a date. Callers pass values that came from
 * a ministry, and a ministry has sent `0000-00-00`; "Invalid Date" where a citizen's
 * date of birth should be is worse than the raw string.
 */
export function formatDay(iso: string, lang: Lang): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return new Intl.DateTimeFormat(lang === 'ar' ? 'ar-AE' : 'en-AE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    numberingSystem: 'latn',
  }).format(parsed);
}
